import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ValidationAutomation,
  ValidationAutomationDecision,
  ValidationAutomationDocument,
  ValidationAutomationQc,
  ValidationAutomationQcVerdict
} from '@percent/admin-dashboard/api/actions/admin/admin.types'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import {
  Accordion,
  Badge,
  BadgeIcon,
  BadgeVariant,
  CardDashboard,
  Flex,
  Heading,
  MarkdownPreview,
  theme,
  Typography
} from '@percent/lemonade'
import styles from './RalphReasoning.module.scss'

const summaryBoxStyle = {
  backgroundColor: theme.scale.primary50,
  border: `1px solid ${theme.scale.primary100}`,
  borderRadius: 8,
  padding: '16px 20px'
}

type RalphReasoningProps = {
  automation: ValidationAutomation
  nonprofitName: string
  qc?: ValidationAutomationQc | null
}

const DECISION_BADGE_VARIANT: Record<ValidationAutomationDecision['decision'], BadgeVariant> = {
  accept: 'positive',
  reject: 'critical',
  merge: 'interactive',
  skip: 'default'
}

const DECISION_BADGE_ICON: Record<ValidationAutomationDecision['decision'], BadgeIcon> = {
  accept: 'approve',
  reject: 'reject',
  merge: 'approve',
  skip: 'approve'
}

const QC_DECISION_BADGE_VARIANT: Record<ValidationAutomationQcVerdict['decision'], BadgeVariant> = {
  approve: 'positive',
  reject: 'critical',
  merge: 'interactive'
}

const QC_DECISION_BADGE_ICON: Record<ValidationAutomationQcVerdict['decision'], BadgeIcon> = {
  approve: 'approve',
  reject: 'reject',
  merge: 'approve'
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <Heading level="h5">{children}</Heading>
}

function DocumentField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Flex gap={4} align="baseline">
      <Typography variant="default-bold">{label}</Typography>
      <Typography variant="default">{children}</Typography>
    </Flex>
  )
}

function DocumentCard({ document }: { document: ValidationAutomationDocument }) {
  const { t } = useTranslation()

  return (
    <li className={styles.documentItem}>
      <Flex gap={8} wrap="wrap" align="center" mb={12}>
        <Typography variant="default-bold">{document.documentType}</Typography>
        {document.organisationName && (
          <Badge variant={document.nameMatchesSubmission ? 'positive' : 'critical'}>
            {t(document.nameMatchesSubmission ? 'typography.ralphNameMatch' : 'typography.ralphNameMismatch')}
          </Badge>
        )}
        {document.registryId && (
          <Badge variant={document.registryIdMatchesSubmission ? 'positive' : 'critical'}>
            {t(
              document.registryIdMatchesSubmission
                ? 'typography.ralphRegistryIdMatch'
                : 'typography.ralphRegistryIdMismatch'
            )}
          </Badge>
        )}
      </Flex>
      <div className={styles.documentFields}>
        {document.organisationName && (
          <DocumentField label={t('typography.ralphDocumentOrganisationName')}>
            {document.organisationName}
          </DocumentField>
        )}
        {document.registryId && (
          <DocumentField label={t('typography.ralphDocumentRegistryId')}>{document.registryId}</DocumentField>
        )}
        {document.issuingAuthority && (
          <DocumentField label={t('typography.ralphDocumentIssuingAuthority')}>
            {document.issuingAuthority}
          </DocumentField>
        )}
        {document.date && (
          <DocumentField label={t('typography.ralphDocumentDate')}>{getDate(document.date)}</DocumentField>
        )}
      </div>
      {document.findings && <Typography variant="default">{document.findings}</Typography>}
    </li>
  )
}

export function RalphReasoning({ automation, nonprofitName, qc }: RalphReasoningProps) {
  const { t } = useTranslation()
  const { response } = automation
  const { decision, summary, reasoning, documentAnalysis } = response

  const documentsCount = documentAnalysis?.documentsReviewed ?? documentAnalysis?.documents?.length ?? 0

  return (
    <CardDashboard className={styles.container} data-testid="ralph-reasoning">
      <Typography variant="h6" className={styles.title}>
        {t('typography.ralphReasoning')}
      </Typography>
      <Flex direction="column" gap={20}>
        <Flex direction="column" gap={12} style={summaryBoxStyle}>
          <Heading level="h5">{t('typography.ralphVerificationSummary', { nonprofitName })}</Heading>
          <div className={styles.decisionGrid}>
            <Typography variant="default-bold">{t('typography.ralphDecision')}</Typography>
            <Badge variant={DECISION_BADGE_VARIANT[decision.decision]} icon={DECISION_BADGE_ICON[decision.decision]}>
              {t(`ralphDecision.${decision.decision}`)}
            </Badge>
            {decision.decision === 'merge' && decision.organisationId && (
              <>
                <Typography variant="default-bold">{t('typography.ralphExisting')}</Typography>
                <Badge variant="default">{decision.organisationId}</Badge>
              </>
            )}
            {decision.decision === 'reject' && decision.rejectionReasonCode && (
              <>
                <Typography variant="default-bold">{t('typography.ralphReason')}</Typography>
                <Badge variant="default">
                  {t(`typography.timeline.status.${decision.rejectionReasonCode}` as LocaleKey, {
                    defaultValue: decision.rejectionReasonCode
                  })}
                </Badge>
              </>
            )}
          </div>
          <Typography variant="default">{summary}</Typography>
        </Flex>

        <Accordion multiple variant="separated">
          <Accordion.Item value="documents">
            <Accordion.Control>
              <SectionLabel>{t('typography.ralphDocumentAnalysis', { count: documentsCount })}</SectionLabel>
            </Accordion.Control>
            <Accordion.Panel>
              <Flex direction="column" gap={8}>
                {documentAnalysis?.summary && <Typography variant="default">{documentAnalysis.summary}</Typography>}
                {documentsCount > 0 && documentAnalysis?.documents ? (
                  <ul className={styles.documentList}>
                    {documentAnalysis.documents.map(doc => (
                      <DocumentCard key={doc.documentKey} document={doc} />
                    ))}
                  </ul>
                ) : (
                  <Typography variant="default">{t('typography.ralphNoDocumentsReviewed')}</Typography>
                )}
              </Flex>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="reasoning">
            <Accordion.Control>
              <SectionLabel>{t('typography.ralphReasoningDetail')}</SectionLabel>
            </Accordion.Control>
            <Accordion.Panel>
              <MarkdownPreview content={reasoning} fontSize="default" className={styles.reasoning} />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {qc && (
          <Flex direction="column" gap={12} style={summaryBoxStyle} data-testid="ralph-reasoning-qc">
            <Heading level="h5">{t('typography.qcReview')}</Heading>
            <div className={styles.decisionGrid}>
              <Typography variant="default-bold">{t('typography.qcVerdict')}</Typography>
              <Badge
                variant={QC_DECISION_BADGE_VARIANT[qc.verdict.decision]}
                icon={QC_DECISION_BADGE_ICON[qc.verdict.decision]}
              >
                {t(`qcVerdict.${qc.verdict.decision}`)}
              </Badge>
              {qc.verdict.decision === 'merge' && (
                <>
                  <Typography variant="default-bold">{t('typography.ralphExisting')}</Typography>
                  <Badge variant="default">{qc.verdict.organisationId}</Badge>
                </>
              )}
              {qc.verdict.decision === 'reject' && (
                <>
                  <Typography variant="default-bold">{t('typography.ralphReason')}</Typography>
                  <Badge variant="default">
                    {t(`typography.timeline.status.${qc.verdict.rejectionReasonCode}` as LocaleKey, {
                      defaultValue: qc.verdict.rejectionReasonCode
                    })}
                  </Badge>
                </>
              )}
              <Typography variant="default-bold">{t('typography.qcSubmittedAt')}</Typography>
              <Typography variant="default">{getDate(qc.createdAt)}</Typography>
              <Typography variant="default-bold">{t('typography.reasoning')}</Typography>
              <Typography variant="default">{qc.reasoning}</Typography>
            </div>
          </Flex>
        )}
      </Flex>
    </CardDashboard>
  )
}
